<template>
  <div class="contact-form">
    <div class="mx-auto max-w-5xl">
      <!-- Single column layout -->
      <div class="bg-white rounded-xl relative">
        
        <!-- Loading Overlay -->
        <div v-if="submitting" class="absolute inset-0 bg-white bg-opacity-80 backdrop-blur-md flex items-center justify-center z-50 rounded-xl">
          <div class="bg-white rounded-2xl p-8 mx-4 text-center">
            <div class="inline-flex items-center justify-center w-16 h-16 bg-red-600 rounded-full mb-4">
              <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
            <h3 class="text-xl font-semibold text-gray-900 mb-2">Bericht wordt verzonden...</h3>
            <p class="text-gray-600 text-sm">Even geduld alstublieft</p>
          </div>
        </div>

        <!-- Success Message (replaces form) -->
        <div v-if="showSuccess" class="p-8 md:p-16 flex items-center justify-center min-h-96">
          <div class="rounded-2xl p-8 mx-4 text-center">
            <div class="inline-flex items-center justify-center w-16 h-16 bg-green-500 rounded-full mb-4">
              <svg class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 class="text-xl font-bold text-gray-900 mb-3">Bedankt voor uw bericht!</h3>
            <p class="text-gray-600 mb-6 text-sm leading-relaxed">
              We nemen zo spoedig mogelijk contact met u op.
            </p>
            <button 
              @click="resetForm"
              class="inline-flex items-center px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors duration-200 text-sm"
            >
              <svg class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              Nieuw bericht
            </button>
          </div>
        </div>

        <!-- Error Message (replaces form) -->
        <div v-if="showError" class="p-8 md:p-16 flex items-center justify-center min-h-96">
          <div class="bg-white rounded-2xl p-8 mx-4 text-center">
            <div class="inline-flex items-center justify-center w-16 h-16 bg-red-500 rounded-full mb-4">
              <svg class="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M12 9v2m0 4h.01" />
              </svg>
            </div>
            <h3 class="text-xl font-bold text-gray-900 mb-3">Er is een fout opgetreden</h3>
            <p class="text-gray-600 mb-6 text-sm leading-relaxed">{{ errorMessage }}</p>
            <button 
              @click="showError = false"
              class="inline-flex items-center px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors duration-200 text-sm"
            >
              <svg class="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Probeer opnieuw
            </button>
          </div>
        </div>

        <!-- Form Content (hidden when success or error is shown) -->
        <div v-if="!showSuccess && !showError" class="p-8 md:p-16" :class="{ 'opacity-30': submitting }">
          <!-- Header -->
          <div class="text-center mb-8">
            <h1 class="text-3xl font-bold text-gray-800 mb-2">
              Hoe kunnen wij u helpen?
            </h1>
            <p class="text-gray-600">Wij horen graag van u.</p>
          </div>

          <!-- Form -->
          <form @submit.prevent="submitForm" class="space-y-8">
            <!-- Vacation Section (at the top) -->
            <div class="space-y-4">
              <h3
                class="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2"
              >
                Vakantie
              </h3>

              <!-- Vakantie Field -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Vakantie <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="form.vakantie"
                  type="text"
                  placeholder="Naam van de vakantie"
                  required
                  :disabled="submitting"
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-theme focus:border-theme transition-colors disabled:bg-gray-100"
                />
              </div>
            </div>

            <!-- Personal Information Section -->
            <div class="space-y-4">
              <h3
                class="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2"
              >
                Persoonlijke gegevens
              </h3>

              <div class="grid md:grid-cols-2 gap-4">
                <!-- Name Field -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Naam <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="form.name"
                    type="text"
                    placeholder="Naam + voornaam"
                    required
                    :disabled="submitting"
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-theme focus:border-theme transition-colors disabled:bg-gray-100"
                  />
                </div>

                <!-- Company Field -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Bedrijf
                  </label>
                  <input
                    v-model="form.company"
                    type="text"
                    placeholder="Optioneel"
                    :disabled="submitting"
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-theme focus:border-theme transition-colors disabled:bg-gray-100"
                  />
                </div>
              </div>

              <div class="grid md:grid-cols-2 gap-4">
                <!-- Phone Field -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Telefoon <span class="text-red-500">*</span>
                  </label>
                  <div class="flex">
                    <select
                      v-model="selectedCountryCode"
                      :disabled="submitting" style="max-width: 100px;"
                      class="px-3 py-3 border border-r-0 border-gray-300 rounded-l-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-theme focus:border-theme text-sm disabled:bg-gray-100"
                    >
                      <option
                        v-for="country in europeanCountries"
                        :key="country.code"
                        :value="country.code"
                      >
                        {{ country.flag }} {{ country.code }}
                      </option>
                    </select>
                    <input
                      v-model="form.phone"
                      type="tel"
                      required
                      :disabled="submitting"
                      class="flex-1 px-4 py-3 border border-gray-300 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-theme focus:border-theme transition-colors disabled:bg-gray-100"
                    />
                  </div>
                </div>

                <!-- Email Field -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Email <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="form.email"
                    type="email"
                    required
                    :disabled="submitting"
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-theme focus:border-theme transition-colors disabled:bg-gray-100"
                  />
                </div>
              </div>

              <!-- Location Field -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Locatie/Postcode <span class="text-red-500">*</span>
                </label>
                <div class="relative">
                  <input
                    v-model="form.location"
                    @input="handleLocationInput"
                    @focus="showSuggestions = true"
                    @blur="hideSuggestions"
                    type="text"
                    placeholder="Bijv. 3500, Hasselt"
                    required
                    :disabled="submitting"
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-theme focus:border-theme transition-colors disabled:bg-gray-100"
                  />

                  <!-- Autocomplete suggestions -->
                  <div
                    v-if="showSuggestions && filteredLocations.length > 0 && !submitting"
                    class="absolute z-50 w-full bg-white border border-gray-300 rounded-lg shadow-sm mt-1 max-h-48 overflow-y-auto"
                  >
                    <div
                      v-for="location in filteredLocations.slice(0, 10)"
                      :key="`${location.zip}-${location.city}`"
                      @mousedown="selectLocation(location)"
                      class="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                    >
                      <div class="flex justify-between items-center">
                        <span class="font-medium"
                          >{{ location.zip }} {{ location.city }}</span
                        >
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Travel Agents Section -->
              <div v-if="(recommendedAgent || showAgentSelection) && !submitting" class="mt-6">
                <!-- Single Agent Display -->
                <div
                  v-if="recommendedAgent && !showAgentSelection"
                  class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden agent-card transition-all duration-300"
                >
                  <!-- Header with icon and title -->
                  <div
                    class="px-6 py-4 flex justify-between items-center bg-gray-100"
                  >
                    <h4 class="text-lg font-semibold flex items-center">
                      <svg
                        class="h-5 w-5 mr-3"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M15 11a3 3 0 11-6 0 3 3 0 616 0z"
                        />
                      </svg>
                      Geselecteerde reisagent
                    </h4>
                    <button
                      v-if="availableAgents.length > 1"
                      @click="showAllAgents"
                      class="text-sm underline transition-colors"
                    >
                      Andere opties bekijken ({{ availableAgents.length - 1 }})
                    </button>
                  </div>

                  <!-- Agent content -->
                  <div class="p-6">
                    <div class="flex items-start gap-4">
                      <!-- Agent Image -->
                      <div class="flex-shrink-0 text-center">
                        <div
                          class="w-20 h-20 rounded-xl overflow-hidden bg-gray-100 border-2 border-white shadow-sm mx-auto"
                        >
                          <img
                            :src="recommendedAgent.avatar"
                            :alt="recommendedAgent.name"
                            class="w-full h-full object-cover"
                            @error="handleImageError"
                          />
                        </div>
                        <!-- Distance below image -->
                        <div
                          class="mt-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-bold shadow-md inline-block"
                        >
                          {{ formatDistance(recommendedAgent.distance) }}
                        </div>
                      </div>

                      <!-- Agent Info -->
                      <div class="flex-1 min-w-0">
                        <h5 class="text-xl font-bold text-gray-900 mb-2">
                          {{ recommendedAgent.name }}
                        </h5>

                        <!-- Address -->
                        <div class="flex items-start gap-2 mb-4">
                          <svg
                            class="h-4 w-4 text-gray-400 mt-1 flex-shrink-0"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                            />
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M15 11a3 3 0 11-6 0 3 3 0 616 0z"
                            />
                          </svg>
                          <div class="text-sm text-gray-600">
                            <p class="font-medium text-gray-800">
                              {{ recommendedAgent.addressLine1 }}
                            </p>
                            <p class="text-gray-500">
                              {{ recommendedAgent.addressLine2 }}
                            </p>
                          </div>
                        </div>

                        <!-- Contact Actions -->
                        <div class="flex flex-wrap gap-4">
                          <a
                            v-if="recommendedAgent.phone"
                            :href="`tel:${recommendedAgent.phone}`"
                            class="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-all duration-200 shadow-md hover:shadow-sm"
                          >
                            <svg
                              class="h-4 w-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                              />
                            </svg>
                            Bel nu
                          </a>

                          <a
                            v-if="recommendedAgent.website"
                            :href="`https://${recommendedAgent.website}`"
                            target="_blank"
                            class="inline-flex items-center gap-2 px-4 py-2 bg-white border-2 border-blue-600 text-blue-600 hover:bg-blue-50 rounded-lg font-medium transition-all duration-200 shadow-md hover:shadow-sm"
                          >
                            <svg
                              class="h-4 w-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                              />
                            </svg>
                            Website
                          </a>
                        </div>
                      </div>
                    </div>

                    <!-- Optional: Add a subtle bottom note -->
                    <div class="mt-4 pt-4 border-t border-gray-100">
                      <p
                        class="text-xs text-gray-500 flex items-center justify-center gap-1"
                      >
                        <svg
                          class="h-3 w-3"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        Deze agent kan u persoonlijk adviseren over uw
                        reisplannen
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Multiple Agents Selection -->
                <div
                  v-if="showAgentSelection"
                  ref="agentSelection"
                  class="bg-white rounded-xl shadow-sm overflow-hidden border"
                  :class="
                    agentError
                      ? 'border-red-500 ring-2 ring-red-200'
                      : 'border-gray-200'
                  "
                >
                  <!-- Header -->
                  <div class="px-6 py-4 bg-gray-100">
                    <h4 class="text-lg font-semibold flex items-center">
                      <svg
                        class="h-5 w-5 mr-3"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M15 11a3 3 0 11-6 0 3 3 0 616 0z"
                        />
                      </svg>
                      Beschikbare reisagenten ({{ availableAgents.length }})
                      <span class="text-red-500 ml-1">*</span>
                    </h4>
                    <p class="text-sm mt-1">
                      Kies de agent die het beste bij u past
                    </p>
                  </div>

                  <!-- Validation message -->
                  <div
                    v-if="agentError"
                    class="px-6 py-3 bg-red-50 border-b border-red-200"
                  >
                    <p class="text-sm text-red-700 flex items-center gap-2">
                      <svg
                        class="h-4 w-4 flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                        />
                      </svg>
                      Gelieve een reisagent te selecteren om verder te gaan.
                    </p>
                  </div>

                  <!-- Agents Grid -->
                  <div class="p-6">
                    <div class="grid gap-4">
                      <div
                        v-for="agent in availableAgents"
                        :key="agent.id"
                        @click="selectAgent(agent)"
                        class="border border-gray-200 rounded-lg p-4 hover:border-red-500 hover:bg-red-50 cursor-pointer transition-all duration-200 group"
                      >
                        <div class="flex items-start gap-4">
                          <!-- Agent Image -->
                          <div class="flex-shrink-0 text-center">
                            <div
                              class="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 border border-gray-200 group-hover:border-red-300"
                            >
                              <img
                                :src="agent.avatar"
                                :alt="agent.name"
                                class="w-full h-full object-cover"
                                @error="handleImageError"
                              />
                            </div>
                            <!-- Distance badge -->
                            <div
                              class="mt-1 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-bold shadow-sm inline-block"
                            >
                              {{ formatDistance(agent.distance) }}
                            </div>
                          </div>

                          <!-- Agent Info -->
                          <div class="flex-1 min-w-0">
                            <h5
                              class="text-lg font-bold text-gray-900 group-hover:text-red-700 mb-1"
                            >
                              {{ agent.name }}
                            </h5>

                            <!-- Address -->
                            <div class="flex items-start gap-2 mb-3">
                              <svg
                                class="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  stroke-width="2"
                                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                />
                                <path
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  stroke-width="2"
                                  d="M15 11a3 3 0 11-6 0 3 3 0 616 0z"
                                />
                              </svg>
                              <div class="text-sm text-gray-600">
                                <p class="font-medium text-gray-800">
                                  {{ agent.addressLine1 }}
                                </p>
                                <p class="text-gray-500">
                                  {{ agent.addressLine2 }}
                                </p>
                              </div>
                            </div>

                            <!-- Quick contact info -->
                            <div
                              class="flex items-center gap-4 text-sm text-gray-500"
                            >
                              <span
                                v-if="agent.phone"
                                class="flex items-center gap-1"
                              >
                                <svg
                                  class="h-3 w-3"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                  />
                                </svg>
                                {{ agent.phone }}
                              </span>
                              <span
                                v-if="agent.website"
                                class="flex items-center gap-1"
                              >
                                <svg
                                  class="h-3 w-3"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                  />
                                </svg>
                                Website
                              </span>
                            </div>
                          </div>

                          <!-- Selection indicator -->
                          <div class="flex-shrink-0">
                            <div
                              class="w-6 h-6 border-2 border-gray-300 rounded-full group-hover:border-red-500 group-hover:bg-red-500 flex items-center justify-center transition-colors"
                            >
                              <svg
                                class="h-3 w-3 text-white opacity-0 group-hover:opacity-100"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  stroke-width="3"
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Help text -->
                    <div class="mt-4 p-3 bg-blue-50 rounded-lg">
                      <p class="text-sm text-blue-700 flex items-center gap-2">
                        <svg
                          class="h-4 w-4 flex-shrink-0"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        Klik op een agent om deze te selecteren. Uw bericht
                        wordt naar de geselecteerde agent verzonden.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Travel Information Section -->
            <div class="space-y-4">
              <h3
                class="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2"
              >
                Reisinformatie
              </h3>

              <!-- Date Fields -->
              <div class="grid md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Vertrekdatum
                  </label>
                  <input
                    v-model="form.departureDate"
                    @change="validateDates"
                    type="date"
                    :min="today"
                    :disabled="submitting"
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-theme focus:border-theme transition-colors disabled:bg-gray-100"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Terugkomst datum
                  </label>
                  <input
                    v-model="form.returnDate"
                    @change="validateDates"
                    type="date"
                    :min="form.departureDate || today"
                    :disabled="submitting"
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-theme focus:border-theme transition-colors disabled:bg-gray-100"
                  />
                </div>
              </div>

              <!-- Number of travelers -->
              <div class="grid md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Aantal volwassenen
                  </label>
                  <input
                    v-model.number="form.adults"
                    type="number"
                    min="1"
                    max="20"
                    :disabled="submitting"
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-theme focus:border-theme transition-colors disabled:bg-gray-100"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Aantal kinderen
                  </label>
                  <input
                    v-model.number="form.children"
                    type="number"
                    min="0"
                    max="20"
                    :disabled="submitting"
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-theme focus:border-theme transition-colors disabled:bg-gray-100"
                  />
                </div>
              </div>

              <!-- Preferences Field -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Voorkeuren
                </label>
                <input
                  v-model="form.preferences"
                  type="text"
                  placeholder="Bijv. all-inclusive, strandvakantie, familie hotel..."
                  :disabled="submitting"
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-theme focus:border-theme transition-colors disabled:bg-gray-100"
                />
              </div>
            </div>

            <!-- Message Section -->
            <div class="space-y-4">
              <h3
                class="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2"
              >
                Uw bericht
                {{
                  recommendedAgent
                    ? `aan ${recommendedAgent.name}`
                    : "aan een reisagent"
                }}
              </h3>

              <!-- Message Field -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Bericht
                </label>
                <textarea
                  v-model="form.message"
                  rows="3"
                  placeholder="Beschrijf uw wensen of stel uw vraag..."
                  :disabled="submitting"
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-theme focus:border-theme transition-colors resize-vertical disabled:bg-gray-100"
                ></textarea>
              </div>

              <!-- Insurance Checkbox -->
              <div class="flex items-start gap-4">
                <input
                  v-model="form.insuranceInfo"
                  type="checkbox"
                  id="insurance"
                  :disabled="submitting"
                  class="mt-1 w-8 h-8 text-theme bg-gray-100 border-gray-300 rounded focus:ring-theme focus:ring-2 disabled:bg-gray-100"
                />
                <label
                  for="insurance"
                  class="text-sm text-gray-600 leading-relaxed"
                >
                  Informeer me vrijblijvend over reisverzekeringsmogelijkheden
                </label>
              </div>
            </div>

            <!-- Submit Button -->
            <div class="pt-4 border-t border-gray-200">
              <button
                type="submit"
                :disabled="submitting"
                class="w-full bg-border-theme font-semibold py-4 px-6 rounded-lg transition-all duration-200 shadow-sm hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="!submitting" class="text-theme">VERZENDEN</span>
                <span v-else class="flex items-center justify-center">
                  <div
                    class="animate-spin rounded-full h-5 w-5 border-b-2 mr-2 text-theme"
                  ></div>
                  Versturen...
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import belgiumZipCodes from "../constants/belgium-zip-code.json";

export default {
  name: "ContactForm",
  data() {
    return {
      form: {
        vakantie: "",
        name: "",
        company: "",
        phone: "",
        email: "",
        location: "",
        departureDate: "",
        returnDate: "",
        adults: 1,
        children: 0,
        preferences: "",
        message: "",
        insuranceInfo: false,
      },

      // Vacation link from URL
      vakantieLink: "",

      // Phone number support
      selectedCountryCode: "+32",
      europeanCountries: [
        { code: "+32", flag: "🇧🇪", name: "Belgium" },
        { code: "+33", flag: "🇫🇷", name: "France" },
        { code: "+49", flag: "🇩🇪", name: "Germany" },
        { code: "+31", flag: "🇳🇱", name: "Netherlands" },
        { code: "+39", flag: "🇮🇹", name: "Italy" },
        { code: "+34", flag: "🇪🇸", name: "Spain" },
        { code: "+43", flag: "🇦🇹", name: "Austria" },
        { code: "+41", flag: "🇨🇭", name: "Switzerland" },
        { code: "+44", flag: "🇬🇧", name: "United Kingdom" },
        { code: "+45", flag: "🇩🇰", name: "Denmark" },
        { code: "+46", flag: "🇸🇪", name: "Sweden" },
        { code: "+47", flag: "🇳🇴", name: "Norway" },
        { code: "+48", flag: "🇵🇱", name: "Poland" },
        { code: "+351", flag: "🇵🇹", name: "Portugal" },
        { code: "+420", flag: "🇨🇿", name: "Czech Republic" },
        { code: "+36", flag: "🇭🇺", name: "Hungary" },
        { code: "+358", flag: "🇫🇮", name: "Finland" },
        { code: "+353", flag: "🇮🇪", name: "Ireland" },
      ],

      // Autocomplete state
      showSuggestions: false,
      filteredLocations: [],
      selectedLocation: null,

      // Form state
      submitting: false,
      showSuccess: false,
      showError: false,
      errorMessage: "",

      // Agent recommendation
      recommendedAgent: null,
      availableAgents: [],
      showAgentSelection: false,
      agentError: false,

      // Data
      locations: belgiumZipCodes,
    };
  },

  computed: {
    shopSettings() {
      return this.$root.shopSettings || {};
    },

    today() {
      return new Date().toISOString().split("T")[0];
    },
  },

  mounted() {
    // Read URL parameters for vakantie and vakantie-link
    const urlParams = new URLSearchParams(window.location.search);
    const vakantie = urlParams.get("vakantie");
    const vakantieLink = urlParams.get("vakantie-link");

    if (vakantie) {
      this.form.vakantie = decodeURIComponent(vakantie);
    }

    if (vakantieLink) {
      this.vakantieLink = decodeURIComponent(vakantieLink);
    }
  },

  methods: {
    handleLocationInput() {
      const query = this.form.location.toLowerCase().trim();

      if (query.length < 2) {
        this.filteredLocations = [];
        this.selectedLocation = null;
        this.recommendedAgent = null;
        return;
      }

      // Filter locations by zip code or city name
      this.filteredLocations = this.locations.filter((location) => {
        return (
          location.zip.toLowerCase().includes(query) ||
          location.city.toLowerCase().includes(query)
        );
      });

      // If exact match found, automatically recommend agent
      const exactMatch = this.locations.find(
        (location) =>
          location.zip === query || location.city.toLowerCase() === query
      );

      if (exactMatch && !this.selectedLocation) {
        this.selectedLocation = exactMatch;
        this.findNearestAgent(exactMatch);
      }
    },

    selectLocation(location) {
      this.form.location = `${location.zip} ${location.city}`;
      this.selectedLocation = location;
      this.showSuggestions = false;
      this.findNearestAgent(location);
    },

    hideSuggestions() {
      // Delay hiding to allow for selection
      setTimeout(() => {
        this.showSuggestions = false;
      }, 200);
    },

    validateDates() {
      if (this.form.departureDate && this.form.returnDate) {
        if (
          new Date(this.form.returnDate) < new Date(this.form.departureDate)
        ) {
          this.form.returnDate = "";
          // Optional: Show a brief error message
          alert("Terugkomstdatum kan niet voor de vertrekdatum zijn.");
        }
      }
    },

    async findNearestAgent(location) {
      this.agentError = false;
      try {
        // Fetch active agencies from our local API
        const apiUrl = this.shopSettings?.apiUrl || "https://selectair-vakanties.uptodatesites.com";
        const response = await fetch(`${apiUrl}/api/admin/travel-agencies/active`);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const allAgencies = await response.json();

        // Calculate distance for each agency and filter by 25km range
        const rangeInMeters = 25000;
        const agenciesWithDistance = allAgencies
          .filter(agency => agency.coordinates?.latitude && agency.coordinates?.longitude)
          .map(agency => ({
            ...agency,
            distance: this.calculateDistance(
              location.lat,
              location.lng,
              agency.coordinates.latitude,
              agency.coordinates.longitude
            )
          }))
          .filter(agency => agency.distance <= rangeInMeters)
          .sort((a, b) => a.distance - b.distance);

        if (agenciesWithDistance.length > 0) {
          this.availableAgents = agenciesWithDistance;

          // If multiple agents found, show selection interface
          if (agenciesWithDistance.length > 1) {
            this.showAgentSelection = true;
            this.recommendedAgent = null; // Clear single agent
          } else {
            // Single agent - show directly like before
            this.recommendedAgent = agenciesWithDistance[0];
            this.showAgentSelection = false;
          }
        } else {
          this.recommendedAgent = null;
          this.availableAgents = [];
          this.showAgentSelection = false;
        }
      } catch (error) {
        console.error("Error finding nearest agent:", error);
        this.recommendedAgent = null;
      }
    },

    // Haversine formula to calculate distance between two coordinates in meters
    calculateDistance(lat1, lon1, lat2, lon2) {
      const R = 6371000; // Earth's radius in meters
      const dLat = this.toRad(lat2 - lat1);
      const dLon = this.toRad(lon2 - lon1);
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return R * c;
    },

    toRad(deg) {
      return deg * (Math.PI / 180);
    },

    async submitForm() {
      this.showError = false;
      this.showSuccess = false;
      this.agentError = false;

      // An agent must be picked whenever the location returned a list
      if (this.availableAgents.length > 0 && !this.recommendedAgent) {
        this.agentError = true;
        this.showAgentSelection = true;
        this.showError = true;
        this.errorMessage = "Gelieve een reisagent te selecteren.";
        this.$nextTick(() => {
          this.$refs.agentSelection?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        });
        return;
      }

      this.submitting = true;

      try {
        // Validate required fields
        if (
          !this.form.vakantie ||
          !this.form.name ||
          !this.form.phone ||
          !this.form.email ||
          !this.form.location
        ) {
          throw new Error("Gelieve alle verplichte velden in te vullen.");
        }

        // Prepare form data
        const formData = {
          ...this.form,
          phone: `${this.selectedCountryCode} ${this.form.phone}`,
          vakantieLink: this.vakantieLink,
          selectedLocation: this.selectedLocation,
          recommendedAgent: this.recommendedAgent,
          submittedAt: new Date().toISOString(),
        };

        // Send email via Resend
        await this.sendEmailNotification(formData);

        // Show success message
        this.showSuccess = true;
      } catch (error) {
        this.showError = true;
        this.errorMessage =
          error.message ||
          "Er is een onverwachte fout opgetreden. Probeer het later opnieuw.";
      } finally {
        this.submitting = false;
      }
    },

    async sendEmailNotification(formData) {
      try {
        // Determine recipient email
        const recipientEmail = this.recommendedAgent?.email || 'info@selectair.be'
        // const recipientEmail = "jeric@uptodatewebdesign.com";
        const recipientName =
          this.recommendedAgent?.name || "Selectair Support";

        // Create payload for backend
        const emailPayload = {
          recipientEmail: recipientEmail,
          recipientName: recipientName,
          subject: `Nieuwe contactaanvraag van ${formData.name}`,
          htmlContent: this.generateEmailTemplate(formData),
        };

        // Send via secure backend endpoint
        const apiUrl = this.shopSettings?.apiUrl || "https://selectair-vakanties.uptodatesites.com";
        const response = await fetch(`${apiUrl}/api/admin/send-email`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(emailPayload),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            `Email failed to send: ${errorData.error || response.statusText}`
          );
        }

        const result = await response.json();
        console.log("Email sent successfully:", result);
      } catch (error) {
        console.error("Email sending error:", error);
        throw new Error(
          "Er was een probleem bij het versturen van uw bericht. Probeer het later opnieuw."
        );
      }
    },

    generateEmailTemplate(formData) {
      const formattedDate = new Date(formData.submittedAt).toLocaleString(
        "nl-BE",
        {
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }
      );

      return `
        <!DOCTYPE html>
        <html lang="nl">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Nieuwe contactaanvraag</title>
          <style>
            body {
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              line-height: 1.6;
              color: #333;
              max-width: 650px;
              margin: 0 auto;
              padding: 20px;
              background-color: #f8f9fa;
            }
            .container {
              background: white;
              border-radius: 12px;
              padding: 35px;
              max-width: 650px;
              box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
              max-width: 100%;
            }
            .header {
              padding: 25px;
              border-radius: 8px;
              text-align: center;
              margin-bottom: 30px;
              border: 2px solid #dc2626;
              color: #dc2626;
            }
            .header h1 {
              margin: 0;
              font-size: 24px;
              font-weight: 600;
            }
            .section {
              margin-bottom: 25px;
              padding: 20px;
              background: #f8f9fa;
              border-radius: 8px;
              border: 2px solid #9e9e9e12;
            }
            .section h2 {
              margin: 0 0 15px 0;
              color: #dc2626;
              font-size: 18px;
              font-weight: 600;
            }
            .field {
              margin-bottom: 12px;
            }
            .field-label {
              font-weight: 600;
              color: #555;
              display: inline-block;
              min-width: 130px;
            }
            .field-value {
              color: #333;
            }
            .agent-section {
              background: #dcdcdca6;
              color: white;
              padding: 20px;
              border-radius: 8px;
              margin: 20px 0;
            }
            .agent-section h3 {
              margin: 0 0 15px 0;
              font-size: 16px;
              color: black;
            }
            .message-box {
              background: white;
              border: 2px solid #e5e7eb;
              border-radius: 8px;
              padding: 15px;
              margin: 10px 0;
              font-style: italic;
              color: #555;
            }
            .footer {
              text-align: center;
              margin-top: 40px;
              padding: 25px;
              background: #f8f9fa;
              border-radius: 8px;
              border: 1px solid #e9ecef;
              color: #6c757d;
              font-size: 14px;
            }
            .footer-button {
              display: inline-block;
              border: 2px solid #dc2626;
              color: #dc2626 !important;
              padding: 12px 24px;
              border-radius: 6px;
              text-decoration: none;
              font-weight: 500;
              margin-top: 15px;
              transition: background-color 0.2s;
            }
            .footer-button:hover {
              background: #dc2626;
              color: white !important;
            }
            .highlight {
              background: #fef3c7;
              padding: 2px 6px;
              border-radius: 4px;
            }
          </style>
        </head>
        <body>
          <div class="container" style="max-width: 650px; margin: 0 auto;">
            <div class="header">
              <h1>📧 Nieuwe Contactaanvraag</h1>
              <p style="margin: 5px 0 0 0; opacity: 0.9;">Selectair Contact Formulier</p>
            </div>

            ${
              formData.vakantie
                ? `
            <div class="section" style="background: #fef3c7; border: 2px solid #f59e0b;">
              <h2>🏖️ Geïnteresseerd in Vakantie</h2>
              <div class="field">
                <span class="field-label">Vakantie:</span>
                <span class="field-value"><strong>${formData.vakantie}</strong></span>
              </div>
              ${
                formData.vakantieLink
                  ? `
              <div class="field">
                <span class="field-label">Bekijk aanbieding:</span>
                <span class="field-value"><a href="${formData.vakantieLink}" style="color: #dc2626;" target="_blank">${formData.vakantieLink}</a></span>
              </div>
              `
                  : ""
              }
            </div>
            `
                : ""
            }

            <div class="section">
              <h2>👤 Persoonlijke Gegevens</h2>
              <div class="field">
                <span class="field-label">Naam:</span>
                <span class="field-value"><strong>${
                  formData.name
                }</strong></span>
              </div>
              ${
                formData.company
                  ? `
              <div class="field">
                <span class="field-label">Bedrijf:</span>
                <span class="field-value">${formData.company}</span>
              </div>
              `
                  : ""
              }
              <div class="field">
                <span class="field-label">Telefoon:</span>
                <span class="field-value">${formData.phone}</span>
              </div>
              <div class="field">
                <span class="field-label">Email:</span>
                <span class="field-value"><a href="mailto:${
                  formData.email
                }" style="color: #dc2626;">${formData.email}</a></span>
              </div>
              <div class="field">
                <span class="field-label">Locatie:</span>
                <span class="field-value">${formData.location}</span>
              </div>
            </div>
            
            <div class="section">
              <h2>✈️ Reisinformatie</h2>
              ${
                formData.departureDate
                  ? `
              <div class="field">
                <span class="field-label">Vertrekdatum:</span>
                <span class="field-value highlight">${new Date(
                  formData.departureDate
                ).toLocaleDateString("nl-BE")}</span>
              </div>
              `
                  : ""
              }
              ${
                formData.returnDate
                  ? `
              <div class="field">
                <span class="field-label">Terugkomst:</span>
                <span class="field-value highlight">${new Date(
                  formData.returnDate
                ).toLocaleDateString("nl-BE")}</span>
              </div>
              `
                  : ""
              }
              ${
                formData.preferences
                  ? `
              <div class="field">
                <span class="field-label">Voorkeuren:</span>
                <span class="field-value">${formData.preferences}</span>
              </div>
              `
                  : ""
              }
              <div class="field">
                <span class="field-label">Aantal volwassenen:</span>
                <span class="field-value">${formData.adults || 1}</span>
              </div>
              <div class="field">
                <span class="field-label">Aantal kinderen:</span>
                <span class="field-value">${formData.children || 0}</span>
              </div>
            </div>

            ${
              formData.message
                ? `
            <div class="section">
              <h2>💬 Bericht</h2>
              <div class="message-box">
                "${formData.message}"
              </div>
            </div>
            `
                : ""
            }
            
            <div class="section">
              <h2>📋 Aanvullende Informatie</h2>
              <div class="field">
                <span class="field-label">Reisverzekering:</span>
                <span class="field-value">${
                  formData.insuranceInfo
                    ? "✅ Ja, wil informatie ontvangen"
                    : "❌ Nee"
                }</span>
              </div>
              <div class="field">
                <span class="field-label">Ingediend op:</span>
                <span class="field-value">${formattedDate}</span>
              </div>
            </div>
            
            ${
              this.recommendedAgent
                ? `
            <div class="agent-section">
              <h3>🎯 Aanbevolen Agent voor deze Klant</h3>
              <div class="field">
                <span class="field-label">Agent:</span>
                <span class="field-value">${this.recommendedAgent.name}</span>
              </div>
              <div class="field">
                <span class="field-label">Adres:</span>
                <span class="field-value">${
                  this.recommendedAgent.addressLine1
                }, ${this.recommendedAgent.addressLine2}</span>
              </div>
              <div class="field">
                <span class="field-label">Afstand:</span>
                <span class="field-value">${this.formatDistance(
                  this.recommendedAgent.distance
                )}</span>
              </div>
              ${
                this.recommendedAgent.phone
                  ? `
              <div class="field">
                <span class="field-label">Telefoon:</span>
                <span class="field-value">${this.recommendedAgent.phone}</span>
              </div>
              `
                  : ""
              }
            </div>
            `
                : ""
            }
            
            <div class="footer">
              <p style="margin: 0 0 8px 0; font-weight: 600; color: #495057;">Selectair Contact Formulier</p>
              <p style="margin: 0 0 15px 0; font-size: 13px;">Automatisch verstuurd via de website</p>
              <a href="mailto:${formData.email}" class="footer-button">
                📧 Antwoord aan klant
              </a>
            </div>
          </div>
        </body>
        </html>
      `;
    },

    resetForm() {
      this.form = {
        vakantie: "",
        name: "",
        company: "",
        phone: "",
        email: "",
        location: "",
        departureDate: "",
        returnDate: "",
        adults: 1,
        children: 0,
        preferences: "",
        message: "",
        insuranceInfo: false,
      };
      this.vakantieLink = "";
      this.selectedLocation = null;
      this.recommendedAgent = null;
      this.availableAgents = [];
      this.showAgentSelection = false;
      this.agentError = false;
      this.showSuccess = false;
      this.showError = false;
      this.submitting = false;
      this.selectedCountryCode = "+32";
    },

    formatDistance(distanceInMeters) {
      const km = (distanceInMeters / 1000).toFixed(1);
      return `${km} km`;
    },

    handleImageError(event) {
      event.target.src =
        "https://uptodatewebdesign.s3.eu-west-3.amazonaws.com/uploads/icon-1741669842731.png";
    },

    selectAgent(agent) {
      this.recommendedAgent = agent;
      this.showAgentSelection = false;
      this.agentError = false;
      this.showError = false;
    },

    showAllAgents() {
      this.showAgentSelection = true;
      this.recommendedAgent = null;
    },
  },
};
</script>

<style scoped>
:root {
  --selectair-text-xs: 12px;
  --selectair-text-sm: 14px;
  --selectair-text-base: 16px;
  --selectair-text-lg: 18px;
  --selectair-text-xl: 20px;
  --selectair-text-2xl: 24px;
  --selectair-text-3xl: 30px;
  --selectair-line-xs: 16px;
  --selectair-line-sm: 20px;
  --selectair-line-base: 24px;
  --selectair-line-lg: 28px;
  --selectair-line-xl: 28px;
  --selectair-line-2xl: 32px;
  --selectair-line-3xl: 36px;
}

.contact-form {
  /* min-height: 100vh; */
}
.bg-border-theme {
  border: 2px solid var(--theme);
  color: var(--theme);
  background-color: transparent;
  transition: all 0.2s ease;
}

.bg-border-theme:hover {
  background-color: var(--theme);
  color: white !important;

  span {
    color: white !important;
  }
}

.bg-theme {
  background-color: var(--theme);
}

.bg-theme-hover {
  background-color: var(--theme-hover);
}

.text-theme {
  color: var(--theme);
}

.hover\:bg-theme-hover:hover {
  background-color: var(--theme-hover);
}

.focus\:ring-theme:focus {
  --tw-ring-color: var(--theme);
}

/* Fix Tailwind text sizes for 10px rem base */
.text-xs {
  font-size: var(--selectair-text-xs) !important;
  line-height: var(--selectair-line-xs) !important;
}

.text-sm {
  font-size: var(--selectair-text-sm) !important;
  line-height: var(--selectair-line-sm) !important;
}

.text-base {
  font-size: var(--selectair-text-base) !important;
  line-height: var(--selectair-line-base) !important;
}

.text-lg {
  font-size: var(--selectair-text-lg) !important;
  line-height: var(--selectair-line-lg) !important;
}

.text-xl {
  font-size: var(--selectair-text-xl) !important;
  line-height: var(--selectair-line-xl) !important;
}

.text-2xl {
  font-size: var(--selectair-text-2xl) !important;
  line-height: var(--selectair-line-2xl) !important;
}

.text-3xl {
  font-size: var(--selectair-text-3xl) !important;
  line-height: var(--selectair-line-3xl) !important;
}

/* Agent card hover effects */
.agent-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
}

/* Form animations */
input:focus,
textarea:focus,
select:focus {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

button {
  transform: translateY(0);
  transition: all 0.2s ease;
}

button:hover:not(:disabled) {
  transform: translateY(-2px);
}

button:active:not(:disabled) {
  transform: translateY(0);
}
.text-3xl {
  font-size: 28px !important;
}
</style>
